import type {
  MRT_Column,
  MRT_ColumnDef,
  MRT_ColumnSizingState,
  MRT_RowData,
  MRT_TableInstance,
  MRT_TableOptions,
  MRT_Updater,
} from "material-react-table";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  columnBounds,
  fitColumnWidths,
  foldColumns,
  type ColumnMeasurement,
  type FitColumn,
} from "_shared/_views/tables/table-column-fit";
import { cellIntrinsicWidth, COLUMN_ID_ATTRIBUTE } from "_shared/_views/tables/table-column-widths";

/** Whether any of a table's columns, or the columns they group, says what kind of value it holds. */
export function hasColumnKinds<TData extends MRT_RowData>(columns: readonly MRT_ColumnDef<TData>[] | undefined): boolean {
  return (columns ?? []).some((column) => column.meta?.kind !== undefined || hasColumnKinds(column.columns));
}

/**
 * The reader's own widths: the entries of `next` that are not what the fit gave.
 *
 * MRT hands back the whole sizing state on every drag, the fitted widths of every other column included.
 * Keeping only what differs is what makes a drag change one column: the others stay fitted, and go on
 * following the pane. A width under the column's minimum is raised to it, so a drag never clips a
 * heading.
 */
export function readerColumnSizing(
  next: MRT_ColumnSizingState,
  fitted: MRT_ColumnSizingState,
  minimums: Readonly<Record<string, number>>,
): MRT_ColumnSizingState {
  const reader: MRT_ColumnSizingState = {};
  for (const [columnId, width] of Object.entries(next)) {
    if (fitted[columnId] === width) continue;
    reader[columnId] = Math.max(width, minimums[columnId] ?? 0);
  }
  return reader;
}

function sameSizing(a: MRT_ColumnSizingState, b: MRT_ColumnSizingState): boolean {
  const aKeys = Object.keys(a);
  return aKeys.length === Object.keys(b).length && aKeys.every((key) => a[key] === b[key]);
}

/** One column's heading and loaded cells, measured where the table is drawn. */
function measureColumn<TData extends MRT_RowData>(
  table: MRT_TableInstance<TData>,
  container: HTMLElement,
  columnId: string,
): ColumnMeasurement {
  const headCell = table.refs.tableHeadCellRefs.current?.[columnId];
  const label = headCell?.querySelector<HTMLElement>(".Mui-TableHeadCell-Content-Wrapper");
  let headingWidth = 0;
  if (headCell && label && label.scrollWidth > 0) {
    const style = getComputedStyle(headCell);
    headingWidth =
      label.scrollWidth + (Number.parseFloat(style.paddingLeft) || 0) + (Number.parseFloat(style.paddingRight) || 0);
  }

  const cells = container.querySelectorAll<HTMLElement>(`tbody [${COLUMN_ID_ATTRIBUTE}="${columnId}"]`);
  return { headingWidth, contentWidths: Array.from(cells, (cell) => cellIntrinsicWidth(cell)) };
}

/** What MRT gives a column that states no `maxSize`. */
const MRT_DEFAULT_MAX_SIZE = 1000;

function fitColumnOf<TData extends MRT_RowData>(
  column: MRT_Column<TData>,
  measurement: ColumnMeasurement | undefined,
): FitColumn {
  const { columnDef } = column;
  const kind = columnDef.meta?.kind;
  const declared = { size: columnDef.size ?? column.getSize(), minSize: columnDef.minSize ?? 0 };
  if (!kind) return { id: column.id, minimum: declared.size, preferred: declared.size, grows: false };
  const fold = columnDef.meta?.fold;
  const bounds = columnBounds({
    id: column.id,
    kind,
    override: columnDef.meta?.width,
    measurement: measurement ?? { headingWidth: 0, contentWidths: [] },
    declared,
  });
  // MRT draws no column wider than its `maxSize`, so room past it would be lost rather than shared.
  const maxSize = columnDef.maxSize ?? MRT_DEFAULT_MAX_SIZE;
  return {
    ...bounds,
    limit: Math.min(bounds.limit ?? maxSize, maxSize),
    ...(fold ? { fold: { into: fold.into, priority: fold.priority } } : {}),
  };
}

/**
 * A list table's fitted column widths, and the reader's own widths over them (#6708).
 *
 * Call it before `useMaterialReactTable` for {@link FittedColumns.options}, and hand the table to
 * {@link useFitColumnsToPane} afterwards: the measuring needs the table drawn.
 */
export function useFittedColumnsState() {
  const [fitted, setFitted] = useState<MRT_ColumnSizingState>({});
  const [minimums, setMinimums] = useState<Record<string, number>>({});
  const [reader, setReader] = useState<MRT_ColumnSizingState>({});
  // Each folded column, and the host whose second line it reads on (#6711).
  const [foldedInto, setFoldedInto] = useState<Readonly<Record<string, string>>>({});
  return { fitted, setFitted, minimums, setMinimums, reader, setReader, foldedInto, setFoldedInto };
}

export type FittedColumns = ReturnType<typeof useFittedColumnsState>;

/**
 * The sizing state MRT is given, and what becomes of a resize.
 *
 * The reader's widths lie over the fitted ones, and a width the table itself controls lies over both.
 */
export function fittedSizingOptions<TData extends MRT_RowData>(
  fit: FittedColumns,
  baseOptions: MRT_TableOptions<TData>,
): Pick<MRT_TableOptions<TData>, "onColumnSizingChange"> & { columnSizing: MRT_ColumnSizingState } {
  const columnSizing = { ...fit.fitted, ...fit.reader, ...baseOptions.state?.columnSizing };
  return {
    columnSizing,
    onColumnSizingChange: (updater: MRT_Updater<MRT_ColumnSizingState>) => {
      const next = typeof updater === "function" ? updater(columnSizing) : updater;
      fit.setReader(readerColumnSizing(next, fit.fitted, fit.minimums));
      baseOptions.onColumnSizingChange?.(updater);
    },
  };
}

function sameFolds(a: Readonly<Record<string, string>>, b: Readonly<Record<string, string>>): boolean {
  const aKeys = Object.keys(a);
  return aKeys.length === Object.keys(b).length && aKeys.every((key) => a[key] === b[key]);
}

function sameMeasurement(previous: ColumnMeasurement | undefined, next: ColumnMeasurement): boolean {
  if (!previous || previous.headingWidth !== next.headingWidth) return false;
  if (previous.contentWidths.length !== next.contentWidths.length) return false;
  return previous.contentWidths.every((width, index) => width === next.contentWidths[index]);
}

/**
 * Measures the table's columns once per data load, and fits them to its pane whenever the pane or the
 * columns showing change.
 *
 * Measured again when the rows change, but not when the reader turns the page: columns that moved under
 * the reader on every page would be harder to read than columns sized to the first one. While the table
 * is loading its cells hold skeletons rather than values, so only the headings are measured.
 */
export function useFitColumnsToPane<TData extends MRT_RowData>(
  table: MRT_TableInstance<TData>,
  fit: FittedColumns,
  isActive: boolean,
) {
  const [measurements, setMeasurements] = useState<Record<string, ColumnMeasurement>>({});
  const [paneWidth, setPaneWidth] = useState<number | undefined>(undefined);
  const measuredPageIndex = useRef<number | undefined>(undefined);
  // The measurements as last set, for the effect to build on without depending on them.
  const latestMeasurements = useRef<Record<string, ColumnMeasurement>>({});
  const { setFitted, setMinimums, setFoldedInto } = fit;
  // A folded column draws no cells, so there is nothing of it to measure: it keeps the measure it had
  // when it was drawn. Read through a ref, so a fold does not set off a measure of its own.
  const foldedColumns = useRef(fit.foldedInto);
  const { foldedInto } = fit;
  useLayoutEffect(() => {
    foldedColumns.current = foldedInto;
  }, [foldedInto]);

  const state = table.getState();
  const isLoading = Boolean(state.isLoading || state.showSkeletons);
  const pageIndex = state.pagination.pageIndex;
  // What says the columns drawn have changed: `getVisibleLeafColumns` answers a new array each time.
  const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id).join(",");
  const data = table.options.data;

  useLayoutEffect(() => {
    const container = table.refs.tableContainerRef.current;
    // A container with no width is a renderer with no layout, where every measurement would be zero.
    if (!isActive || !container || container.clientWidth < 1) return;
    const columnIds = visibleColumnIds.split(",");
    if (isLoading) {
      // The cells hold skeletons, but the headings are already drawn: measuring them now keeps every
      // heading whole from the first paint, rather than from whenever the rows arrive. A column that
      // was measured in full already keeps that measurement.
      const unmeasured = columnIds.filter((columnId) => !(columnId in latestMeasurements.current));
      if (unmeasured.length < 1) return;
      const headings = { ...latestMeasurements.current };
      for (const columnId of unmeasured) headings[columnId] = { ...measureColumn(table, container, columnId), contentWidths: [] };
      latestMeasurements.current = headings;
      setMeasurements(headings);
      return;
    }
    // A turned page keeps the widths the first page was fitted to, but a column it has never measured
    // is measured all the same: MRT adds its actions column a render after the rows, and the page index
    // can settle in that same render. A measure that saw no rows fitted nothing, so it does not count.
    const sawRows = Object.values(latestMeasurements.current).some((measured) => measured.contentWidths.length > 0);
    const isPageTurn = sawRows && measuredPageIndex.current !== undefined && measuredPageIndex.current !== pageIndex;
    // The page the widths were measured on moves only with a full measure: a table that turns its page
    // before the new page's rows arrive is still on a turned page when they land.
    if (!isPageTurn) measuredPageIndex.current = pageIndex;
    const next: Record<string, ColumnMeasurement> = isPageTurn ? { ...latestMeasurements.current } : {};
    for (const columnId of Object.keys(foldedColumns.current)) {
      const kept = latestMeasurements.current[columnId];
      if (kept) next[columnId] = kept;
    }
    const toMeasure = columnIds.filter((columnId) => !(columnId in next));
    if (toMeasure.length < 1) return;
    for (const columnId of toMeasure) next[columnId] = measureColumn(table, container, columnId);
    latestMeasurements.current = next;
    setMeasurements(next);
  }, [data, isActive, isLoading, pageIndex, table, visibleColumnIds]);

  // The rows' content can change with no new data, so two kinds of column are measured again whenever
  // it does. An actions column: its controls wait on the reader's permissions, and collapse into a menu
  // by the viewport. And a column whose last measure found nothing in its cells: a load can be measured
  // a render before its rows draw their content, and fitting it on its headings alone squeezes out the
  // actions column. A column already measured with content is left alone, since a data column can draw
  // itself by its own width, as Team does, and measuring it again would chase its own tail.
  useEffect(() => {
    const container = table.refs.tableContainerRef.current;
    if (!isActive || !container || typeof MutationObserver === "undefined") return;
    let frame: number | undefined;
    const remeasure = () => {
      frame = undefined;
      // Skeletons are not content: the measure when the rows land is what counts.
      const { isLoading, showSkeletons } = table.getState();
      if (container.clientWidth < 1 || isLoading || showSkeletons) return;
      let next = latestMeasurements.current;
      for (const { id: columnId } of table.getVisibleLeafColumns()) {
        if (columnId in foldedColumns.current) continue;
        const isActions = table.getColumn(columnId)?.columnDef.meta?.kind === "actions";
        const foundNothing = !next[columnId]?.contentWidths.some((width) => width > 0);
        if (!isActions && !foundNothing) continue;
        const measured = measureColumn(table, container, columnId);
        if (!measured.contentWidths.some((width) => width > 0) || sameMeasurement(next[columnId], measured)) continue;
        next = { ...next, [columnId]: measured };
      }
      if (next === latestMeasurements.current) return;
      latestMeasurements.current = next;
      setMeasurements(next);
    };
    const observer = new MutationObserver(() => {
      if (frame === undefined) frame = requestAnimationFrame(remeasure);
    });
    observer.observe(container, { characterData: true, childList: true, subtree: true });
    return () => {
      observer.disconnect();
      if (frame !== undefined) cancelAnimationFrame(frame);
    };
  }, [isActive, table, visibleColumnIds]);

  useEffect(() => {
    const container = table.refs.tableContainerRef.current;
    if (!isActive || !container || typeof ResizeObserver === "undefined") return;
    // An observer reports the size it finds as soon as it starts observing, so this is the first
    // measurement as well as every later one.
    const observer = new ResizeObserver(() => setPaneWidth(container.clientWidth || undefined));
    observer.observe(container);
    return () => observer.disconnect();
  }, [isActive, table]);

  useLayoutEffect(() => {
    if (!isActive) return;
    const visibleColumns = table.getVisibleLeafColumns() as MRT_Column<TData>[];
    // The columns drawn can run ahead of what was measured: MRT adds its own columns, such as the
    // actions column, to the table a render after the rows, and a fit without them squeezes them out.
    // So a drawn column that was never measured is measured before anything is fitted.
    const container = table.refs.tableContainerRef.current;
    const unmeasured = visibleColumns.filter((column) => !(column.id in measurements));
    if (unmeasured.length > 0 && !isLoading && container && container.clientWidth >= 1) {
      const next = { ...latestMeasurements.current };
      for (const column of unmeasured) next[column.id] = measureColumn(table, container, column.id);
      latestMeasurements.current = next;
      setMeasurements(next);
      return;
    }
    const columns = visibleColumns.map((column) => fitColumnOf(column, measurements[column.id]));
    // Short of every minimum, the lowest-priority columns fold onto their hosts before the table scrolls.
    const { shown, foldedInto: nextFoldedInto } = foldColumns(paneWidth, columns);
    const nextFitted = fitColumnWidths(paneWidth, shown);
    const nextMinimums = Object.fromEntries(shown.map((column) => [column.id, column.minimum]));
    setFitted((previous) => (sameSizing(previous, nextFitted) ? previous : nextFitted));
    setMinimums((previous) => (sameSizing(previous, nextMinimums) ? previous : nextMinimums));
    setFoldedInto((previous) => (sameFolds(previous, nextFoldedInto) ? previous : nextFoldedInto));
  }, [isActive, isLoading, measurements, paneWidth, setFitted, setFoldedInto, setMinimums, table, visibleColumnIds]);
}
